import React, { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import FilterBar, { FilterSummary } from '../components/FilterBar';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { athleticFieldsData } from '../data/athleticFieldsData';
import './AthleticFields.css';
import { usePersistentFlag, usePersistentSet } from '../utils/expandedState';

const TIMELINES = [
  { value: '0–3 years', label: '0–3 years' },
  { value: '0–6 years', label: '0–6 years' },
  { value: '0–12 years', label: '0–12 years' },
  { value: '3–6 years', label: '3–6 years' },
  { value: 'Ongoing', label: 'Ongoing' },
];

const STATUSES = [
  { value: 'New (2020)', label: 'New (2020)' },
  { value: 'Revised (2020)', label: 'Revised (2020)' },
  { value: 'Remaining from 2006', label: 'Remaining from 2006' },
];

function getAllItems(data) {
  const items = [];
  data.themes.forEach(theme => {
    theme.items.forEach(it => items.push(it));
  });
  return items;
}

function statusClass(status) {
  if (status === 'New (2020)') return 'af-status-new';
  if (status === 'Revised (2020)') return 'af-status-revised';
  if (status === 'Remaining from 2006') return 'af-status-remaining';
  return '';
}

function AthleticFields() {
  const [sel, setSel] = useState({ timeline: new Set(), status: new Set() });
  const [expandedThemes, setExpandedThemes] = usePersistentSet('athletic-fields:expandedThemes', () => new Set(athleticFieldsData.themes.map(t => t.id)));
  const [expandedItems, setExpandedItems] = usePersistentSet('athletic-fields:expandedItems');
  const [overviewExpanded, setOverviewExpanded] = usePersistentFlag('athletic-fields:overviewExpanded');
  const [statusKeyExpanded, setStatusKeyExpanded] = usePersistentFlag('athletic-fields:statusKeyExpanded');

  useEffect(() => {
    document.title = 'Athletic Fields Master Plan';
  }, []);

  const allItems = useMemo(() => getAllItems(athleticFieldsData), []);

  // Unique key for an action item (theme + rec id + index) — several items share
  // a recommendation id (e.g. two rows under 1.A), so index within the theme
  // disambiguates them for expand/collapse state.
  const keyOf = (themeId, idx) => `${themeId}::${idx}`;

  const dimensions = useMemo(() => [
    { key: 'timeline', label: 'Timeline', values: TIMELINES.map(t => ({
      value: t.value, label: t.label,
      count: allItems.filter(i => i.timeline === t.value).length,
    })).filter(v => v.count > 0) },
    { key: 'status', label: 'Status', values: STATUSES.map(s => ({
      value: s.value, label: s.label,
      count: allItems.filter(i => i.status === s.value).length,
    })) },
  ], [allItems]);

  const toggleValue = (key, value) => setSel(prev => {
    const next = new Set(prev[key]);
    if (next.has(value)) next.delete(value); else next.add(value);
    return { ...prev, [key]: next };
  });
  const clearDimension = (key) => setSel(prev => ({ ...prev, [key]: new Set() }));
  const clearAllFilters = () => setSel({ timeline: new Set(), status: new Set() });

  const matchesItem = useMemo(() => {
    return (it) => {
      if (sel.timeline.size && !sel.timeline.has(it.timeline)) return false;
      if (sel.status.size && !sel.status.has(it.status)) return false;
      return true;
    };
  }, [sel]);

  const filteredThemes = useMemo(() => {
    return athleticFieldsData.themes.map(theme => ({
      ...theme,
      items: theme.items
        .map((it, idx) => ({ it, idx }))
        .filter(({ it }) => matchesItem(it))
    })).filter(theme => theme.items.length > 0);
  }, [matchesItem]);

  const totalFiltered = useMemo(() => {
    return filteredThemes.reduce((sum, theme) => sum + theme.items.length, 0);
  }, [filteredThemes]);

  const toggleTheme = (themeId) => {
    setExpandedThemes(prev => {
      const next = new Set(prev);
      if (next.has(themeId)) next.delete(themeId);
      else next.add(themeId);
      return next;
    });
  };

  const toggleItem = (k) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedThemes(new Set(athleticFieldsData.themes.map(t => t.id)));
    const allKeys = new Set();
    athleticFieldsData.themes.forEach(theme => {
      theme.items.forEach((_, idx) => allKeys.add(keyOf(theme.id, idx)));
    });
    setExpandedItems(allKeys);
    setOverviewExpanded(true);
    setStatusKeyExpanded(true);
  };

  const collapseAll = () => {
    setExpandedThemes(new Set());
    setExpandedItems(new Set());
    setOverviewExpanded(false);
    setStatusKeyExpanded(false);
  };

  const renderMetaField = (label, value) => (
    value ? (
      <div className="af-meta-field">
        <span className="af-meta-label">{label}:</span> {value}
      </div>
    ) : null
  );

  const renderItemBody = (it) => (
    <div className="af-item-body">
      <div className="af-item-category">Part of {it.id}: {it.category}</div>
      <div className="af-item-meta">
        {renderMetaField('Timeline', it.timelineDetail ? `${it.timeline} — ${it.timelineDetail}` : it.timeline)}
        {renderMetaField('Responsibility', it.responsibility)}
        {renderMetaField('Financial impact', it.financialImpact)}
        {renderMetaField('Projected capital costs', it.capitalCosts)}
        {renderMetaField('Public benefit', it.publicBenefit)}
      </div>
    </div>
  );

  return (
    <div className="af-page">
      <a href="#main-content" className="af-skip-link">Skip to main content</a>
      <Header
        title="Athletic Fields Master Plan"
        creditExtra="website design by Michael Rubenstein"
      />

      <main id="main-content" tabIndex={-1} className="af-content">
        <div className="af-intro">
          <p className="af-updated">
            Town of Brookline · Parks, Open Space and Recreation · 2020 Needs Assessment &amp; Master Plan
          </p>
        </div>

        <div className="af-controls">
          <FilterBar
            allCount={allItems.length}
            dimensions={dimensions}
            selected={sel}
            onToggleValue={toggleValue}
            onClearDimension={clearDimension}
            onClearAll={clearAllFilters}
            trailing={
              <>
                <button className="af-expand-btn" onClick={expandAll}>Expand All</button>
                <button className="af-expand-btn" onClick={collapseAll}>Collapse All</button>
              </>
            }
          />
        </div>

        <FilterSummary
          count={totalFiltered}
          noun="recommendation"
          nounPlural="recommendations"
          dimensions={dimensions}
          selected={sel}
          onClearAll={clearAllFilters}
        />

        <div className="af-preamble-sections">
          <div className="af-preamble">
            <button
              className={`af-preamble-header ${overviewExpanded ? 'expanded' : ''}`}
              onClick={() => setOverviewExpanded(!overviewExpanded)}
              aria-expanded={overviewExpanded}
            >
              <span className={`af-chevron${overviewExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="af-preamble-title">About This Plan</span>
            </button>
            {overviewExpanded && (
              <div className="af-preamble-body">
                {athleticFieldsData.introduction.overview.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>

          <div className="af-preamble">
            <button
              className={`af-preamble-header ${statusKeyExpanded ? 'expanded' : ''}`}
              onClick={() => setStatusKeyExpanded(!statusKeyExpanded)}
              aria-expanded={statusKeyExpanded}
            >
              <span className={`af-chevron${statusKeyExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="af-preamble-title">About the Recommendations &amp; Status Key</span>
            </button>
            {statusKeyExpanded && (
              <div className="af-preamble-body">
                {athleticFieldsData.introduction.itemsNote.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
                <ul className="af-status-key">
                  {athleticFieldsData.legend.status.map(s => (
                    <li key={s.code}>
                      <span className={`af-status-badge ${statusClass(s.code)}`}>{s.code}</span>
                      <span className="af-status-key-label">{s.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="af-themes">
          {filteredThemes.map(theme => (
            <div key={theme.id} className="af-theme">
              <h2 className="af-heading"><button
                className={`af-theme-header ${expandedThemes.has(theme.id) ? 'expanded' : ''}`}
                onClick={() => toggleTheme(theme.id)}
                aria-expanded={expandedThemes.has(theme.id)}
              >
                <span className={`af-chevron af-chevron-light${expandedThemes.has(theme.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                <div className="af-theme-header-text">
                  <span className="af-theme-name">Theme {theme.number}: {theme.name}</span>
                  {theme.description && (
                    <span className="af-theme-desc">{theme.description}</span>
                  )}
                </div>
                <span className="af-theme-count" aria-hidden="true">{theme.items.length}</span>
              </button></h2>

              {expandedThemes.has(theme.id) && (
                <div className="af-theme-body">
                  {theme.items.map(({ it, idx }) => {
                    const k = keyOf(theme.id, idx);
                    const isExpanded = expandedItems.has(k);
                    return (
                      <div key={k} className="af-item">
                        <h3 className="af-heading"><button
                          className={`af-item-header ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleItem(k)}
                          aria-expanded={isExpanded}
                        >
                          <span className={`af-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                          <span className="af-item-id">{it.id}</span>
                          <span className="af-item-title">{it.title}</span>
                          <span className={`af-status-badge small ${statusClass(it.status)}`}>{it.status}</span>
                        </button></h3>
                        {isExpanded && renderItemBody(it)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="af-source">
          <p>
            Source: Town of Brookline, Parks, Open Space and Recreation — 2020 Athletic Fields Needs Assessment
            and Master Plan (November {athleticFieldsData.planYear}; prepared by GreenPlay, LLC with RRC Associates).
            This page presents the plan’s Recommendations and Action Plan (the athletic-field-relevant themes of
            the 2020 Strategic Master Plan Update); it omits the Tiered Athletic Field System, the Decision Matrix,
            and the field inventory and condition assessments.
          </p>
        </div>

        <DisclaimerFooter>
          <p>
            For source information from the Town of Brookline, go to the{' '}
            <a href="https://www.brooklinema.gov/2282/Parks-Open-Spaces" target="_blank" rel="noopener noreferrer">
              Parks &amp; Open Spaces<span className="visually-hidden"> (opens in new tab)</span>
            </a>
            {' '}web page on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default AthleticFields;
